import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  AddCircleOutline as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  AccessTime as TimeIcon,
  PriorityHigh as PriorityIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  ErrorOutline as ErrorIcon,
  BarChart as BarChartIcon,
  SortByAlpha as SortIcon,
  Business as BusinessIcon,
  PersonOutline as PersonIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { base_url, getToken } from "../../config/config";
import { tokens } from "../../theme";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import ReklamationStats from "./ReklamationStats";
import ProcessImprovementForm from "../ProcessImpovements/ProcessImprovementForm";

const ReklamationList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const token = getToken();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // Define colors directly in the component
  // const colors = {
  //   primary: {
  //     100: '#d0d1d5',
  //     200: '#a1a4ab',
  //     300: '#727681',
  //     400: '#1F2A40',
  //     500: '#141b2d',
  //   },
  //   grey: {
  //     100: '#e0e0e0',
  //     200: '#c2c2c2',
  //     300: '#a3a3a3',
  //     400: '#858585',
  //     500: '#666666',
  //     600: '#4d4d4d',
  //     700: '#333333',
  //     800: '#1a1a1a',
  //     900: '#0a0a0a',
  //   },
  //   greenAccent: {
  //     100: '#dbf5ee',
  //     200: '#b7ebde',
  //     300: '#94e2cd',
  //     400: '#70d8bd',
  //     500: '#4cceac',
  //     600: '#3da58a',
  //     700: '#2e7c67',
  //     800: '#1e5245',
  //     900: '#0f2922',
  //   },
  //   redAccent: {
  //     100: '#f8dcdb',
  //     200: '#f1b9b7',
  //     300: '#e99592',
  //     400: '#e2726e',
  //     500: '#db4f4a',
  //     600: '#af3f3b',
  //     700: '#832f2c',
  //     800: '#58201e',
  //     900: '#2c100f',
  //   },
  //   blueAccent: {
  //     100: '#e1e2fe',
  //     200: '#c3c6fd',
  //     300: '#a4a9fc',
  //     400: '#868dfb',
  //     500: '#6870fa',
  //     600: '#535ac8',
  //     700: '#3e4396',
  //     800: '#2a2d64',
  //     900: '#151632',
  //   },
  //   orange: {
  //     400: '#FB8C00',
  //     500: '#EF6C00',
  //   }
  // };

  // State variables
  const [reklamations, setReklamations] = useState([]);
  const [filteredReklamations, setFilteredReklamations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    priority: null,
    status: null,
    processType: null,
  });

  // Process improvement states
  const [isProcessImprovementDialogOpen, setIsProcessImprovementDialogOpen] =
    useState(false);
  const [selectedReklamation, setSelectedReklamation] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch reklamations on component mount
  useEffect(() => {
    fetchReklamations();
  }, []);

  // Apply filters and search whenever reklamations, search term or tab changes
  useEffect(() => {
    applyFiltersAndSearch();
  }, [reklamations, searchTerm, currentTab, filters, sortBy, sortDirection]);

  // Fetch all reklamations
  const fetchReklamations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${base_url}/v1/getAllReklamations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReklamations(response.data);
    } catch (error) {
      console.error("Error fetching reklamations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and search to reklamations
  const applyFiltersAndSearch = () => {
    let filtered = [...reklamations];

    // Apply search term
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (reklamation) =>
          (reklamation.reklamationId &&
            reklamation.reklamationId
              .toLowerCase()
              .includes(lowerCaseSearch)) ||
          (reklamation.description &&
            reklamation.description.toLowerCase().includes(lowerCaseSearch)) ||
          (reklamation.anr &&
            reklamation.anr.toLowerCase().includes(lowerCaseSearch)) ||
          (reklamation.recipient &&
            (reklamation.recipient.firstName?.toLowerCase()
              .includes(lowerCaseSearch) ||
              reklamation.recipient.lastName?.toLowerCase()
              .includes(lowerCaseSearch) ||
              reklamation.recipient.name?.toLowerCase()
              .includes(lowerCaseSearch)
            )
          )
      );
    }

    // Apply tab filter
    if (currentTab === 1) {
      filtered = filtered.filter((r) => r.status === "open");
    } else if (currentTab === 2) {
      filtered = filtered.filter((r) => r.status === "in-progress");
    } else if (currentTab === 3) {
      filtered = filtered.filter((r) => r.status === "completed");
    }

    // Apply additional filters
    if (filters.priority) {
      filtered = filtered.filter((r) => r.priority === filters.priority);
    }

    if (filters.status) {
      filtered = filtered.filter((r) => r.status === filters.status);
    }

    if (filters.processType) {
      filtered = filtered.filter((r) => r.processType === filters.processType);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let valueA, valueB;

      switch (sortBy) {
        case "date":
          valueA = new Date(a.date || a.createdAt);
          valueB = new Date(b.date || b.createdAt);
          break;
        case "priority":
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          valueA = priorityOrder[a.priority] || 999;
          valueB = priorityOrder[b.priority] || 999;
          break;
        case "id":
          valueA = a.reklamationId || "";
          valueB = b.reklamationId || "";
          break;
        case "status":
          const statusOrder = { open: 0, "in-progress": 1, completed: 2 };
          valueA = statusOrder[a.status] || 999;
          valueB = statusOrder[b.status] || 999;
          break;
        default:
          valueA = a[sortBy] || "";
          valueB = b[sortBy] || "";
      }

      const comparison =
        typeof valueA === "string" && typeof valueB === "string"
          ? valueA.localeCompare(valueB)
          : valueA - valueB;

      return sortDirection === "asc" ? comparison : -comparison;
    });

    setFilteredReklamations(filtered);
  };

  // Format date string
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd.MM.yyyy", { locale: de });
    } catch (error) {
      return "Ungültiges Datum";
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle sort menu
  const handleSortClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setAnchorEl(null);
  };

  const handleSortSelect = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection("desc");
    }
    handleSortClose();
  };

  // Handle filter menu
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: prev[filterType] === value ? null : value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      priority: null,
      status: null,
      processType: null,
    });
    handleFilterClose();
  };

  // Navigate to create new reklamation
  const handleCreateNew = () => {
    navigate("/reklamation/new");
  };

  // Navigate to reklamation detail
  const handleViewDetail = (id) => {
    navigate(`/reklamation/${id}`);
  };

  // Handle creating a process improvement from a reklamation
  const handleCreateProcessImprovement = (reklamation) => {
    setSelectedReklamation(reklamation);
    setIsProcessImprovementDialogOpen(true);
  };

  // Handle closing the process improvement dialog
  const handleCloseProcessImprovementDialog = () => {
    setIsProcessImprovementDialogOpen(false);
    setSelectedReklamation(null);
  };

  // Handle process improvement creation success
  const handleProcessImprovementCreated = (newProcess) => {
    setIsProcessImprovementDialogOpen(false);
    setSelectedReklamation(null);
    setNotification({
      open: true,
      message: "Prozessverbesserung erfolgreich erstellt!",
      severity: "success",
    });

    // Navigate to the process improvement page
    navigate("/process-improvements");
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon sx={{ color: colors.greenAccent[500] }} />;
      case "in-progress":
        return <HourglassIcon sx={{ color: colors.blueAccent[400] }} />;
      case "open":
      default:
        return <ErrorIcon sx={{ color: colors.redAccent[600] }} />;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return colors.greenAccent[500];
      case "in-progress":
        return colors.blueAccent[400];
      case "open":
      default:
        return colors.redAccent[600];
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case "completed":
        return "Abgeschlossen";
      case "in-progress":
        return "In Bearbeitung";
      case "open":
      default:
        return "Offen";
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return colors.redAccent[500];
      case "medium":
        return colors.orange[500];
      case "low":
      default:
        return colors.greenAccent[500];
    }
  };

  // Get priority label
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "high":
        return "Hoch";
      case "medium":
        return "Mittel";
      case "low":
      default:
        return "Niedrig";
    }
  };

  // Get process type color
  const getProcessTypeColor = (type) => {
    return type === "external"
      ? colors.blueAccent[500]
      : colors.greenAccent[500];
  };

  // Get process type label
  const getProcessTypeLabel = (type) => {
    return type === "external" ? "Extern" : "Intern";
  };

  // Get process type icon
  const getProcessTypeIcon = (type) => {
    return type === "external" ? <BusinessIcon /> : <PersonIcon />;
  };

  // Data grid columns
  const columns = [
    {
      field: "reklamationId",
      headerName: "ID",
      flex: 0.7,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "date",
      headerName: "Datum",
      flex: 0.8,
      valueGetter: (params) => params.row.date || params.row.createdAt,
      renderCell: (params) => (
        <Typography variant="body2">{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "processType",
      headerName: "Typ",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          icon={getProcessTypeIcon(params.value)}
          label={getProcessTypeLabel(params.value)}
          size="small"
          sx={{
            backgroundColor: getProcessTypeColor(params.value),
            color: "#fff",
          }}
        />
      ),
    },
    {
      field: "priority",
      headerName: "Priorität",
      flex: 0.8,
      renderCell: (params) => (
        <Chip
          icon={<PriorityIcon />}
          label={getPriorityLabel(params.value)}
          size="small"
          sx={{
            backgroundColor: getPriorityColor(params.value),
            color: "#fff",
          }}
        />
      ),
    },
    {
      field: "anr",
      headerName: "ANR",
      flex: 0.8,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "—"}</Typography>
      ),
    },
    {
      field: "description",
      headerName: "Beschreibung",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={getStatusLabel(params.value)}
          size="small"
          sx={{
            backgroundColor: getStatusColor(params.value),
            color: "#fff",
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Aktionen",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleViewDetail(params.row._id)}
            sx={{
              backgroundColor: colors.grey[700],
              color: colors.grey[100],
              fontSize: "12px",
              fontWeight: "bold",
              padding: "5px 10px",
              "&:hover": {
                backgroundColor: colors.grey[600],
              },
            }}
          >
            Details
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleCreateProcessImprovement(params.row)}
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              fontSize: "12px",
              fontWeight: "bold",
              padding: "5px 10px",
              "&:hover": {
                backgroundColor: colors.greenAccent[500],
              },
            }}
          >
            ➡️ Prozessverbesserung
          </Button>
        </Box>
      ),
    },
  ];

  // Calculate statistics for the header cards
  const totalReklamations = reklamations.length;
  const openReklamations = reklamations.filter(
    (r) => r.status === "open"
  ).length;
  const inProgressReklamations = reklamations.filter(
    (r) => r.status === "in-progress"
  ).length;
  const completedReklamations = reklamations.filter(
    (r) => r.status === "completed"
  ).length;
  const highPriorityReklamations = reklamations.filter(
    (r) => r.priority === "high"
  ).length;

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h2" fontWeight="bold">
            Reklamationen
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            Übersicht aller Reklamationen und Verbesserungsmöglichkeiten
          </Typography>
        </Box>
        <Box display="flex">
          <Tooltip title="Statistiken anzeigen">
            <IconButton
              onClick={() => setShowStats(!showStats)}
              sx={{
                backgroundColor: showStats
                  ? colors.primary[300]
                  : colors.primary[400],
                color: colors.grey[100],
                fontSize: "14px",
                fontWeight: "bold",
                padding: "10px 20px",
                marginRight: "10px",
                "&:hover": {
                  backgroundColor: colors.grey[600],
                },
              }}
            >
              <BarChartIcon />
            </IconButton>
          </Tooltip>
          <Button
            onClick={handleCreateNew}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: colors.greenAccent[500],
              },
            }}
          >
            Neue Reklamation
          </Button>
        </Box>
      </Box>

      {/* STATISTICS CARDS */}
      <Grid container spacing={2} sx={{ mt: 2, mb: showStats ? 1 : 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.primary[300], height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle" color={colors.grey[900]}>
                Gesamtzahl
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                {totalReklamations}
              </Typography>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                <Badge
                  color="primary"
                  badgeContent={filteredReklamations.length}
                  max={999}
                  showZero
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: colors.blueAccent[400],
                      color: colors.grey[900],
                    },
                  }}
                >
                  <Typography variant="body2" color={colors.grey[900]}>
                    Gefiltert
                  </Typography>
                </Badge>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.redAccent[600], height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle" color={colors.grey[900]}>
                Offen
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                {openReklamations}
              </Typography>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                <ErrorIcon />
                <Typography
                  variant="body2"
                  color={colors.grey[900]}
                  sx={{ ml: 1 }}
                >
                  Bedarf Aufmerksamkeit
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.blueAccent[400], height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle" color={colors.grey[900]}>
                In Bearbeitung
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                {inProgressReklamations}
              </Typography>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                <HourglassIcon />
                <Typography
                  variant="body2"
                  color={colors.grey[900]}
                  sx={{ ml: 1 }}
                >
                  Aktiv in Arbeit
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.greenAccent[400], height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle" color={colors.grey[900]}>
                Abgeschlossen
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                {completedReklamations}
              </Typography>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                <CheckCircleIcon />
                <Typography
                  variant="body2"
                  color={colors.grey[900]}
                  sx={{ ml: 1 }}
                >
                  Erfolgreich gelöst
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.orange[400], height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle" color={colors.grey[900]}>
                Hohe Priorität
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                {highPriorityReklamations}
              </Typography>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 1 }}
                color={colors.grey[900]}
              >
                <PriorityIcon />
                <Typography
                  variant="body2"
                  color={colors.grey[900]}
                  sx={{ ml: 1 }}
                >
                  Dringende Fälle
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* STATISTICS CHARTS (CONDITIONAL) */}
      {showStats && <ReklamationStats />}

      {/* TABS AND TOOLBAR */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="reklamation tabs"
          textColor="secondary"
          indicatorColor="secondary"
        >
          <Tab label="Alle" />
          <Tab label={`Offen (${openReklamations})`} />
          <Tab label={`In Bearbeitung (${inProgressReklamations})`} />
          <Tab label={`Abgeschlossen (${completedReklamations})`} />
        </Tabs>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Suchen..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<SortIcon />}
            onClick={handleSortClick}
            sx={{
              borderColor: colors.grey[100],
              color: colors.grey[100],
              "&:hover": {
                borderColor: colors.primary[300],
                backgroundColor: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            Sortieren
          </Button>

          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={handleFilterClick}
            color={
              Object.values(filters).some((v) => v !== null)
                ? "secondary"
                : "primary"
            }
            sx={{
              borderColor: Object.values(filters).some((v) => v !== null)
                ? colors.greenAccent[400]
                : colors.grey[100],
              color: Object.values(filters).some((v) => v !== null)
                ? colors.greenAccent[400]
                : colors.grey[100],
              "&:hover": {
                borderColor: Object.values(filters).some((v) => v !== null)
                  ? colors.greenAccent[300]
                  : colors.primary[300],
                backgroundColor: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            Filter {Object.values(filters).some((v) => v !== null) && "(Aktiv)"}
          </Button>
        </Box>

        {/* Sort Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleSortClose}
          PaperProps={{
            elevation: 3,
            sx: {
              bgcolor: colors.primary[400],
              color: colors.grey[100],
            },
          }}
        >
          <MenuItem onClick={() => handleSortSelect("date")}>
            <TimeIcon sx={{ mr: 1 }} />
            Datum{" "}
            {sortBy === "date" && (sortDirection === "asc" ? "(↑)" : "(↓)")}
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect("priority")}>
            <PriorityIcon sx={{ mr: 1 }} />
            Priorität{" "}
            {sortBy === "priority" && (sortDirection === "asc" ? "(↑)" : "(↓)")}
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect("reklamationId")}>
            <FilterIcon sx={{ mr: 1 }} />
            ID{" "}
            {sortBy === "reklamationId" &&
              (sortDirection === "asc" ? "(↑)" : "(↓)")}
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect("status")}>
            <HourglassIcon sx={{ mr: 1 }} />
            Status{" "}
            {sortBy === "status" && (sortDirection === "asc" ? "(↑)" : "(↓)")}
          </MenuItem>
        </Menu>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
          PaperProps={{
            elevation: 3,
            sx: {
              width: 250,
              bgcolor: colors.primary[400],
              color: colors.grey[100],
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Priority
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Chip
                label="Hoch"
                onClick={() => handleFilterSelect("priority", "high")}
                sx={{
                  bgcolor:
                    filters.priority === "high"
                      ? colors.redAccent[500]
                      : "transparent",
                  border: `1px solid ${colors.redAccent[500]}`,
                  color:
                    filters.priority === "high"
                      ? "#fff"
                      : colors.redAccent[500],
                  "&:hover": { opacity: 0.8 },
                }}
                size="small"
              />
              <Chip
                label="Mittel"
                onClick={() => handleFilterSelect("priority", "medium")}
                sx={{
                  bgcolor:
                    filters.priority === "medium"
                      ? colors.orange[500]
                      : "transparent",
                  border: `1px solid ${colors.orange[500]}`,
                  color:
                    filters.priority === "medium" ? "#fff" : colors.orange[500],
                  "&:hover": { opacity: 0.8 },
                }}
                size="small"
              />
              <Chip
                label="Niedrig"
                onClick={() => handleFilterSelect("priority", "low")}
                sx={{
                  bgcolor:
                    filters.priority === "low"
                      ? colors.greenAccent[500]
                      : "transparent",
                  border: `1px solid ${colors.greenAccent[500]}`,
                  color:
                    filters.priority === "low"
                      ? "#fff"
                      : colors.greenAccent[500],
                  "&:hover": { opacity: 0.8 },
                }}
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 1, borderColor: colors.grey[800] }} />

          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Status
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Chip
                label="Offen"
                onClick={() => handleFilterSelect("status", "open")}
                sx={{
                  bgcolor:
                    filters.status === "open"
                      ? colors.redAccent[500]
                      : "transparent",
                  border: `1px solid ${colors.redAccent[500]}`,
                  color:
                    filters.status === "open" ? "#fff" : colors.redAccent[500],
                  "&:hover": { opacity: 0.8 },
                }}
                size="small"
              />
              <Chip
                label="In Bearbeitung"
                onClick={() => handleFilterSelect("status", "in-progress")}
                sx={{
                  bgcolor:
                    filters.status === "in-progress"
                      ? colors.blueAccent[500]
                      : "transparent",
                  border: `1px solid ${colors.blueAccent[500]}`,
                  color:
                    filters.status === "in-progress"
                      ? "#fff"
                      : colors.blueAccent[500],
                  "&:hover": { opacity: 0.8 },
                }}
                size="small"
              />
              <Chip
                label="Abgeschlossen"
                onClick={() => handleFilterSelect("status", "completed")}
                sx={{
                  bgcolor:
                    filters.status === "completed"
                      ? colors.greenAccent[300]
                      : "transparent",
                  border: `1px solid ${colors.greenAccent[300]}`,
                  color:
                    filters.status === "completed"
                      ? "#fff"
                      : colors.greenAccent[300],
                  "&:hover": { opacity: 0.8 },
                }}
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 1, borderColor: colors.grey[800] }} />

          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Prozesstyp
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Chip
                icon={
                  <PersonIcon
                    sx={{
                      color:
                        filters.processType === "internal"
                          ? "#fff"
                          : colors.greenAccent[500],
                    }}
                  />
                }
                label="Intern"
                onClick={() => handleFilterSelect("processType", "internal")}
                sx={{
                  bgcolor:
                    filters.processType === "internal"
                      ? colors.greenAccent[500]
                      : "transparent",
                  border: `1px solid ${colors.greenAccent[500]}`,
                  color:
                    filters.processType === "internal"
                      ? "#fff"
                      : colors.greenAccent[500],
                  "&:hover": { opacity: 0.8 },
                }}
                size="small"
              />
              <Chip
                icon={
                  <BusinessIcon
                    sx={{
                      color:
                        filters.processType === "external"
                          ? "#fff"
                          : colors.blueAccent[500],
                    }}
                  />
                }
                label="Extern"
                onClick={() => handleFilterSelect("processType", "external")}
                sx={{
                  bgcolor:
                    filters.processType === "external"
                      ? colors.blueAccent[500]
                      : "transparent",
                  border: `1px solid ${colors.blueAccent[500]}`,
                  color:
                    filters.processType === "external"
                      ? "#fff"
                      : colors.blueAccent[500],
                  "&:hover": { opacity: 0.8 },
                }}
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 1, borderColor: colors.grey[800] }} />

          <Box sx={{ px: 2, py: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={clearFilters}
              sx={{
                borderColor: colors.grey[300],
                color: colors.grey[300],
                "&:hover": {
                  borderColor: colors.grey[100],
                  color: colors.grey[100],
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                },
              }}
              size="small"
            >
              Filter zurücksetzen
            </Button>
          </Box>
        </Menu>
      </Box>

      {/* MAIN CONTENT - DATA GRID */}
      <Box
        sx={{
          height: "calc(100vh - 350px)",
          width: "100%",
          "& .MuiDataGrid-root": {
            // backgroundColor: 'transparent'
          },
          "& .MuiDataGrid-cell": {
            // borderBottom: `1px solid ${colors.primary[300]}`,
          },
          "& .MuiDataGrid-columnHeaders": {
            border: `1px solid ${colors.primary[300]}`,
          },
          "& .MuiDataGrid-virtualScroller": {},
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : filteredReklamations.length > 0 ? (
          <DataGrid
            rows={filteredReklamations}
            columns={columns}
            getRowId={(row) => row._id}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            components={{
              NoRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="h6" color={colors.grey[500]}>
                    Keine Reklamationen gefunden
                  </Typography>
                  <Typography variant="body2" color={colors.grey[500]}>
                    Versuchen Sie andere Suchkriterien oder erstellen Sie eine
                    neue Reklamation
                  </Typography>
                </Box>
              ),
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              backgroundColor: colors.primary[500],
              borderRadius: 1,
            }}
          >
            <Typography variant="h5" color={colors.grey[400]}>
              Keine Reklamationen gefunden
            </Typography>
            <Typography
              variant="body1"
              color={colors.grey[500]}
              sx={{ mt: 1, mb: 3 }}
            >
              {searchTerm
                ? "Ihre Suche ergab keine Ergebnisse. Versuchen Sie andere Suchkriterien."
                : currentTab > 0
                ? "Es gibt keine Reklamationen mit dem ausgewählten Status."
                : "Es wurden noch keine Reklamationen erstellt."}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              sx={{
                backgroundColor: colors.greenAccent[600],
                "&:hover": {
                  backgroundColor: colors.greenAccent[500],
                },
              }}
            >
              Neue Reklamation erstellen
            </Button>
          </Box>
        )}
      </Box>

      {/* Process Improvement Dialog */}
      <Dialog
        open={isProcessImprovementDialogOpen}
        onClose={handleCloseProcessImprovementDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            borderRadius: "10px",
            boxShadow: 10,
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${colors.primary[300]}` }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <TrendingUpIcon sx={{ color: colors.greenAccent[500], mr: 1 }} />
              <Typography
                variant="h5"
                component="span"
                fontWeight="bold"
                color={colors.greenAccent[500]}
              >
                Prozessverbesserung aus Reklamation erstellen
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseProcessImprovementDialog}
              sx={{ color: colors.grey[400] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedReklamation && (
            <Box mb={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[300],
                  borderColor: colors.primary[200],
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color={colors.grey[100]}
                >
                  Reklamation Details
                </Typography>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color={colors.grey[400]}>
                      Reklamation ID
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      color={colors.grey[100]}
                    >
                      {selectedReklamation.reklamationId || "Nicht verfügbar"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color={colors.grey[400]}>
                      Status
                    </Typography>
                    <Chip
                      icon={getStatusIcon(selectedReklamation.status)}
                      label={getStatusLabel(selectedReklamation.status)}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(
                          selectedReklamation.status
                        ),
                        color: "#fff",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color={colors.grey[400]}>
                      Priorität
                    </Typography>
                    <Chip
                      icon={<PriorityIcon />}
                      label={getPriorityLabel(selectedReklamation.priority)}
                      size="small"
                      sx={{
                        backgroundColor: getPriorityColor(
                          selectedReklamation.priority
                        ),
                        color: "#fff",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color={colors.grey[400]}>
                      Beschreibung
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      color={colors.grey[100]}
                    >
                      {selectedReklamation.description ||
                        "Keine Beschreibung vorhanden"}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          <ProcessImprovementForm
            onProcessCreated={handleProcessImprovementCreated}
            colors={colors}
            initialData={{
              title: selectedReklamation
                ? `Verbesserung für: ${selectedReklamation.reklamationId}`
                : "",
              description: selectedReklamation
                ? `Prozessverbesserungsvorschlag basierend auf Reklamation: ${
                    selectedReklamation.reklamationId
                  }\n\nReklamationsbeschreibung: ${
                    selectedReklamation.description || ""
                  }`
                : "",
              relatedReklamationId: selectedReklamation?._id,
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReklamationList;
